import React from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, CheckCircle2, Clock } from "lucide-react";

/**
 * The Four Pillars of Black Labor Power — featured strip on the LandingPage.
 *
 * Each pillar is paired with a Memphis partner organization, displayed as a
 * co-branded card. Bayard Rustin's partnership with the Rustin Institute is
 * still in progress and is rendered with a "Partnership Pending" badge.
 *
 * Sits directly under TheSouthHomeBanner on the landing page.
 */
export const FourPillarsStrip = () => {
  const pillars = [
    {
      slug: "randolph",
      name: "A. Philip Randolph",
      dates: "1889 — 1979",
      role: "Father of the Modern Civil Rights Movement",
      partner: "APRI Memphis",
      partnerSub: "A. Philip Randolph Institute",
      partnerStatus: "confirmed",
      accent: "from-stone-900 via-stone-800 to-stone-900",
      accentText: "text-amber-300",
      accentBadge: "bg-amber-500/20 border-amber-400/40 text-amber-300",
      href: "/pillars/randolph",
    },
    {
      slug: "rustin",
      name: "Bayard Rustin",
      dates: "1912 — 1987",
      role: "The Strategist",
      partner: "Bayard Rustin Center for Social Justice",
      partnerSub: "Princeton, NJ - partnership pending",
      partnerStatus: "pending",
      accent: "from-purple-950 via-purple-900 to-slate-900",
      accentText: "text-rose-300",
      accentBadge: "bg-rose-500/20 border-rose-400/40 text-rose-300",
      href: "/pillars/rustin",
    },
    {
      slug: "lawson",
      name: "Dr. James M. Lawson Jr.",
      dates: "1928 — 2024",
      role: "The Teacher of Nonviolence",
      partner: "Centenary United Methodist Church",
      partnerSub: "Memphis - Lawson's home pulpit (1968)",
      partnerStatus: "confirmed",
      accent: "from-slate-900 via-slate-800 to-slate-900",
      accentText: "text-amber-300",
      accentBadge: "bg-amber-500/20 border-amber-400/40 text-amber-300",
      href: "/dr-lawson",
    },
    {
      slug: "lucy",
      name: 'William "Bill" Lucy',
      dates: "1933 — 2024",
      role: "The Engine of AFSCME",
      partner: "CBTU Memphis",
      partnerSub: "Coalition of Black Trade Unionists",
      partnerStatus: "confirmed",
      accent: "from-emerald-950 via-emerald-900 to-slate-900",
      accentText: "text-emerald-300",
      accentBadge: "bg-emerald-500/20 border-emerald-400/40 text-emerald-300",
      href: "/pillars/lucy",
    },
  ];

  return (
    <section
      data-testid="four-pillars-strip"
      className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white py-14 border-y-4 border-amber-500"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-bold tracking-wider mb-3">
            <Flame className="w-3.5 h-3.5" />
            AMERICAN LABOR POWER  •  THE MEMPHIS LINEAGE
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
            The Four Pillars
          </h2>
          <p className="text-amber-200/90 text-base sm:text-lg max-w-3xl mx-auto italic mb-3">
            Black leaders whose work built the labor movement for every worker in America.
          </p>
          <p className="text-slate-300 text-base sm:text-lg max-w-3xl mx-auto">
            Randolph taught Rustin. Rustin taught Lawson. Lawson trained the marshals of 1968.
            Lucy printed the signs.  This is the unbroken chain — and these are the Memphis
            partners carrying it forward.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p) => (
            <Link
              key={p.slug}
              to={p.href}
              data-testid={`four-pillars-strip-card-${p.slug}`}
              className={`group bg-gradient-to-br ${p.accent} rounded-2xl border border-white/10 hover:border-white/30 hover:-translate-y-1 transition-all shadow-lg hover:shadow-2xl overflow-hidden flex flex-col`}
            >
              {/* Top - the pillar */}
              <div className="p-6 flex-1">
                <div className={`text-[10px] font-bold tracking-widest mb-2 ${p.accentText}`}>
                  {p.role.toUpperCase()}
                </div>
                <h3 className="font-serif font-bold text-xl leading-tight">{p.name}</h3>
                <p className="text-white/60 text-sm mt-1">{p.dates}</p>
              </div>

              {/* Bottom - partner co-brand */}
              <div className="border-t border-white/15 p-5 bg-black/20">
                <div className="text-[10px] font-bold tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5">
                  IN PARTNERSHIP WITH
                  {p.partnerStatus === "confirmed" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                      <CheckCircle2 className="w-2.5 h-2.5" /> CONFIRMED
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-amber-500/20 text-amber-300 border border-amber-400/30">
                      <Clock className="w-2.5 h-2.5" /> PENDING
                    </span>
                  )}
                </div>
                <div className="font-bold text-white text-sm leading-tight">{p.partner}</div>
                <div className="text-white/60 text-xs mt-0.5">{p.partnerSub}</div>
                <div className={`mt-3 inline-flex items-center gap-2 text-sm font-semibold ${p.accentText} group-hover:gap-3 transition-all`}>
                  Read page
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Hub link */}
        <div className="mt-10 text-center flex flex-wrap justify-center gap-3">
          <Link
            to="/four-pillars"
            data-testid="four-pillars-strip-hub-link"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-6 py-3 rounded-lg shadow-md transition-all"
          >
            <Flame className="w-5 h-5" />
            See the full Four Pillars hub
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/four-pillars#join-the-legacy"
            data-testid="four-pillars-strip-join-link"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-3 rounded-lg shadow-md transition-all"
          >
            Join the Legacy — CBTU &amp; APRI
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FourPillarsStrip;
